import supabase from "../../supabase/supabase";
import { bankService } from "../bankService";

const BANK_ACCOUNTS_TABLE = "bank_accounts";

/**
 * Invoke an existing Supabase Edge Function.
 *
 * Plaid access tokens and encryption keys are never
 * exposed to the frontend.
 */
const invokeFunction = async (
  name,
  body,
  failureMessage
) => {
  const { data, error } = await supabase.functions.invoke(
    name,
    { body }
  );

  if (error) {
    let message = error.message || failureMessage;

    // Attempt to extract the actual backend error.
    if (error.context) {
      try {
        const details = await error.context.json();

        message =
          details?.error ||
          details?.message ||
          message;
      } catch {
        // Keep the original error message.
      }
    }

    throw new Error(`${failureMessage}: ${message}`);
  }

  if (!data) {
    throw new Error(
      `${failureMessage}: No response was received.`
    );
  }

  // Some existing functions return a link_token
  // rather than an explicit success flag.
  if (
    data.success === false ||
    (data.success !== true && !data.link_token)
  ) {
    throw new Error(
      data.error || failureMessage
    );
  }

  return data;
};

/**
 * Client Banking Service
 *
 * Reuses existing ProfitYo banking infrastructure.
 * Does not modify Accounting Firm banking logic.
 */
export const clientBankingService = {
  /**
   * Get connected bank accounts belonging to the
   * selected client organization.
   */
  async getAccounts(organizationId) {
    if (!organizationId) {
      throw new Error(
        "Select an active client organization."
      );
    }

    await bankService.verifyOrganizationAccess(
      organizationId
    );

    const { data, error } = await supabase
      .schema("accounting")
      .from(BANK_ACCOUNTS_TABLE)
      .select(`
        id,
        organization_id,
        account_id,
        institution_name,
        account_name,
        account_type,
        mask,
        balance,
        plaid_item_id,
        plaid_account_id,
        is_active,
        last_sync_at,
        created_at,
        updated_at
      `)
      .eq("organization_id", organizationId)
      .eq("is_active", true)
      .order("created_at", {
        ascending: false,
      });

    if (error) {
      throw new Error(
        `Unable to load connected bank accounts: ${error.message}`
      );
    }

    return data || [];
  },

  /**
   * Initialize Plaid Link.
   *
   * Edge Function:
   * plaid-create-link-token
   */
  async createLinkToken(organizationId) {
    if (!organizationId) {
      throw new Error(
        "Select an active client organization."
      );
    }

    await bankService.verifyOrganizationAccess(
      organizationId
    );

    return invokeFunction(
      "plaid-create-link-token",
      {
        organizationId,
      },
      "Unable to prepare a secure Plaid connection"
    );
  },

  /**
   * Exchange Plaid public_token for a secure
   * server-side connection.
   *
   * Edge Function:
   * plaid-exchange-token
   *
   * The backend encrypts the Plaid access token.
   */
  async exchangePublicToken({
    publicToken,
    organizationId,
    metadata,
  }) {
    if (!publicToken) {
      throw new Error(
        "Plaid did not return a connection token."
      );
    }

    if (!organizationId) {
      throw new Error(
        "Select an active client organization."
      );
    }

    await bankService.verifyOrganizationAccess(
      organizationId
    );

    return invokeFunction(
      "plaid-exchange-token",
      {
        publicToken,
        organizationId,
        metadata: metadata || {},
      },
      "Unable to complete the Plaid connection"
    );
  },

  /**
   * Synchronize connected bank accounts.
   *
   * Edge Function:
   * plaid-sync-accounts
   *
   * Uses the existing Accounting Firm-compatible
   * backend without changing its behavior.
   */
  async syncAccounts({
    organizationId,
    itemId,
  }) {
    if (!organizationId) {
      throw new Error(
        "Select an active client organization."
      );
    }

    if (!itemId) {
      throw new Error(
        "This connection has no readable Plaid Item ID. Account synchronization is unavailable."
      );
    }

    await bankService.verifyOrganizationAccess(
      organizationId
    );

    return invokeFunction(
      "plaid-sync-accounts",
      {
        organizationId,
        itemId,
      },
      "Unable to synchronize bank accounts"
    );
  },

  /**
   * Synchronize transactions for ONE selected
   * bank account.
   *
   * Edge Function:
   * plaid-sync-transactions
   *
   * This preserves the existing staging workflow.
   */
  async syncTransactions({
    organizationId,
    account,
  }) {
    if (!organizationId) {
      throw new Error(
        "Select an active client organization."
      );
    }

    if (
      !account?.id ||
      account.organization_id !== organizationId
    ) {
      throw new Error(
        "The selected bank account does not belong to this business."
      );
    }

    if (!account.account_id) {
      throw new Error(
        "Map this bank account to a ProfitYo account before synchronizing transactions."
      );
    }

    if (!account.plaid_item_id) {
      throw new Error(
        "This connection has no readable Plaid Item ID. Transaction synchronization is unavailable."
      );
    }

    await bankService.verifyOrganizationAccess(
      organizationId
    );

    return invokeFunction(
      "plaid-sync-transactions",
      {
        organizationId,
        itemId: account.plaid_item_id,
        bankAccountId: account.id,
      },
      "Unable to synchronize bank transactions"
    );
  },
};